import {
    combineLatest,
    from,
    ReplaySubject,
    Subject,
    Subscription,
    throwError
} from 'rxjs';
import { catchError, first, map, take, takeUntil } from 'rxjs/operators';
import { SbFormContextService } from './../services/sb-form-context.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import {
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    HostListener,
    Input,
    OnDestroy,
    Renderer2,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environment';
import {
    SbHost,
    SbLayout,
    SbPanel,
    SbRequestMethod,
    SbWidgetLayout,
    SbWidgetType
} from '@models';
import {
    SbEvent,
    SbEventBusService,
    SbEventTopic,
    SbPrintService,
    SbWidgetService
} from '../services';
import { SbWidgetDirective } from '../../sb-widget.directive';
import { SbPanelService } from './../services/sb-panel.service';
import { UiQuery } from '../../shared/state/ui/ui.query';
import { TRANSACTIONS_FORMS } from 'src/app/shared/services/amend/consts';

@Component({
    selector: 'app-sb-panel',
    templateUrl: './sb-panel.component.html',
    styleUrls: ['./sb-panel.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SbPanelComponent implements OnDestroy {
    @Input() panelId: string;
    @Input() tabId: number;
    sbPanel: SbWidgetDirective;
    @ViewChild(SbWidgetDirective) set setSbPanel(sbPanel: SbWidgetDirective) {
        this.sbPanel = sbPanel;
        this.loadComponent();
    }
    allGridsEmpty = true;
    title: string;
    divider: boolean;
    hidePanelHeader: boolean;
    collapse: boolean;
    disableCollapseBelowBreakpoint: boolean;
    enableToggling: boolean;
    widgetLayouts: SbWidgetLayout[];
    isStandaloneForm: boolean;
    subscriptions: Subscription[] = [];
    enablePrinting$ = new ReplaySubject(1);
    printLayout$ = this.printService.isPrinting$;
    mobileScreen$ = this.uiQuery.viewportSize$.pipe(
        map((size) => size === 'xs' || size === 'sm')
    );

    showPrintPreview$ = combineLatest([
        this.enablePrinting$,
        this.printLayout$,
        this.mobileScreen$
    ]).pipe(
        map(
            ([enablePrinting, printLayout, mobileScreen]) =>
                enablePrinting && !printLayout && !mobileScreen
        )
    );
    display: boolean = true;
    unsubscribe$ = new Subject();

    public gridDataEvents: {
        [key: string]: { panelId: string; widgetId: string; isEmpty: boolean };
    } = {};
    private isSynchronizing: boolean = true;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private sbPanelService: SbPanelService,
        private sbWidgetService: SbWidgetService,
        private formContextService: SbFormContextService,
        private breakpointObserver: BreakpointObserver,
        private _renderer: Renderer2,
        private route: ActivatedRoute,
        private location: Location,
        private eventBus: SbEventBusService,
        private printService: SbPrintService,
        private uiQuery: UiQuery
    ) {}

    ngOnDestroy() {
        this.subscriptions.forEach((s) => {
            if (s) {
                s.unsubscribe();
            }
        });

        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    loadComponent() {
        if (
            this.panelId === 'account-performance-overview' ||
            this.panelId === 'top-holdings-panel'
        ) {
            return;
        }
        if (this.panelId) {
            combineLatest([
                from(this.sbPanelService.getAPanel(this.panelId)),
                this.route.queryParamMap
            ])
                .pipe(
                    take(1),
                    catchError((err) =>
                        throwError(
                            () =>
                                new Error(
                                    'Could not load panel with ID: ' +
                                        this.panelId +
                                        '. Reason: ' +
                                        err
                                )
                        )
                    )
                )
                .subscribe(([p, qParams]) => {
                    if (TRANSACTIONS_FORMS.includes(this.panelId)) {
                        if (qParams.get('groupId')) {
                            p.headerText = p.headerText.replace('Register', 'Edit');
                        } else {
                            p.headerText = p.headerText.replace('Edit', 'Register');
                        }
                    }
                    this.renderPanel(p);
                    this.registerBreakPointSubscriber();
                });
        } else {
            console.error('No panelId provided, could not render panel.');
        }
    }

    private registerBreakPointSubscriber() {
        const sub = this.breakpointObserver
            .observe([`(max-width: ${environment.mobileScreenBreakpoint})`])
            .subscribe((result) => {
                this.enableToggling =
                    result.matches && !this.disableCollapseBelowBreakpoint;
                this.collapse = result.matches && !this.disableCollapseBelowBreakpoint;
            });
        this.subscriptions.push(sub);
    }

    async renderPanel(sbPanel: SbPanel) {
        // Get a list of widgets from sbPanel
        this.title = sbPanel?.headerText;
        this.divider = sbPanel?.divider;
        this?.enablePrinting$.next(sbPanel?.enablePrinting || false);
        if (sbPanel.enablePrinting) {
            this.printService.checkPrintingEnables(true);
        }
        const isInTabbedView = this.tabId ? true : this.tabId === 0 ? true : false;
        this.hidePanelHeader = sbPanel.hidePanelHeader || isInTabbedView;
        this.isStandaloneForm = sbPanel.isStandaloneForm;
        this.disableCollapseBelowBreakpoint =
            sbPanel.disableCollapseBelowBreakpoint ||
            sbPanel.isStandaloneForm ||
            isInTabbedView;
        this.widgetLayouts = sbPanel.widgetLayouts ? sbPanel.widgetLayouts : [];

        this.addWidgetsFromPanelContext(sbPanel);
    }

    async addWidgetsFromPanelContext(sbPanel: SbPanel) {
        if (!sbPanel) {
            return;
        }
        const viewContainerRef = this.sbPanel.viewContainerRef;
        viewContainerRef.clear();

        const widgetIds = SbPanelComponent.widgetIdsFromContext(sbPanel);
        const id = this.route.snapshot.paramMap.get('id');
        const widgets = widgetIds.map((wId) =>
            this.createWidgetComponent(wId, id, viewContainerRef)
        );
        const widgetComponents = await Promise.all(widgets);
        widgetComponents.forEach((wc) => {
            const widgetId = wc.instance.context?.widgetId;
            if (
                !SbPanelComponent.isHeaderTextExcludedHere(wc) ||
                SbPanelComponent.isExcludedFromBeingHidden(widgetId)
            ) {
                this.insertWidgetIntoDomWithHeader(
                    viewContainerRef,
                    wc,
                    widgetId,
                    sbPanel
                );
            } else {
                this.listenForGridDataSet(wc);

                this.addWidgetLayoutClasses(widgetId, sbPanel, wc.location.nativeElement);
            }
        });
    }

    private listenForGridDataSet(wc: ComponentRef<any>) {
        //this will be used in emit at the end.
        let widgetId = undefined;

        this.eventBus
            .getObservable(SbEventTopic.GridDataSet)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((event) => {
                if (
                    event != null &&
                    !SbPanelComponent.isExcludedFromBeingHidden(event.source) &&
                    event.source === wc.instance.context?.widgetId
                ) {
                    this.gridDataEvents[event.source] = {
                        panelId: this.panelId,
                        widgetId: event.source,
                        isEmpty: event.payload.rowDataLength === 0
                    };

                    this.display = !this.areAllGridsEmpty(this.panelId);

                    //This one is for print layout
                    this.eventBus.emit({
                        payload: {
                            widgetId: event.source,
                            panelId: this.panelId,
                            display: event.payload.rowDataLength !== 0
                        },
                        source: 'SbPanelComponent GridDataSet',
                        topic: SbEventTopic.DisplayOutputSet
                    });
                }
            });
        //Exclusion for positions-report. This is run just at the beginning.
        //positions-report's widget id is not same with event.source.
        //It has only one widget to hide so we are setting its initial value to false.
        if (this.panelId === 'positions-report') {
            this.display = false;
        }
    }

    private static isExcludedFromBeingHidden(eventSource: string) {
        return [
            'list-limits',
            'list-transactions',
            'list-positions',
            'view-positions',
            'list-issuers',
            'list-issuer-contact-persons',
            'list-organisations',
            'list-persons',
            'list-accounts'
        ].includes(eventSource);
    }

    async createWidgetComponent(
        widgetId: string,
        id: string,
        viewContainerRef: ViewContainerRef
    ): Promise<ComponentRef<any>> {
        const widgetDefinition = await this.sbWidgetService.resolveWidget(widgetId);
        const widgetContext =
            widgetDefinition.widgetType === SbWidgetType.FormWidget
                ? await this.formContextService
                      .getFormConfig(widgetId, id)
                      .pipe(first())
                      .toPromise()
                : await this.sbWidgetService.resolveContext(widgetId);

        if (
            widgetDefinition.widgetId === 'list-transactions' &&
            !widgetContext.contextMenuItems.find(
                (menuItem) => menuItem.name === 'Edit Group'
            )
        ) {
            widgetContext.contextMenuItems.push({
                name: 'Edit Group',
                accessRight: 'CANCEL_TRANSACTION',
                guard: "return data.transactionStatus !== 'CANCEL' && data.isAmendable",
                action: {
                    requestMapping: [
                        {
                            source: 'transactionGroupId',
                            target: 'transactionGroupId'
                        }
                    ],
                    path: {
                        hostEnum: SbHost.GatewayBaseUrl,
                        resource: 'transactions',
                        httpMethod: SbRequestMethod.AMEND
                    }
                },
                colDef: { headerName: 'Edit Group' }
            });
        }
        const resolvedComponent = this.sbPanelService.resolveComponent(
            widgetDefinition.widgetType
        );
        const componentFactory =
            this.componentFactoryResolver?.resolveComponentFactory(resolvedComponent);
        const componentRef = viewContainerRef.createComponent(componentFactory);
        componentRef.instance.context = widgetContext;

        return componentRef;
    }

    static widgetIdsFromContext(sbPanel: SbPanel): string[] {
        return sbPanel.widgetLayouts && sbPanel.widgetLayouts.length > 0
            ? sbPanel.widgetLayouts.map((lo) => lo.widgetId)
            : sbPanel.widgetIds;
    }

    insertWidgetIntoDomWithHeader(
        viewContainerRef: ViewContainerRef,
        componentRef: ComponentRef<any>,
        widgetId: string,
        sbPanel: SbPanel
    ): any {
        const _el = componentRef.location;
        const parent = _el.nativeElement.parentNode;
        // This statement has to be exactly here
        viewContainerRef.insert(componentRef.hostView);

        // 1. Skapa div
        const divElement = this.configureWidgetHeader(componentRef);

        this.addWidgetLayoutClasses(widgetId, sbPanel, divElement);

        // 2. Lägg in widget i diven
        this._renderer.appendChild(divElement, _el.nativeElement);
        this._renderer.appendChild(parent, divElement);
    }

    private configureWidgetHeader(componentRef: ComponentRef<any>) {
        const divElement = this._renderer.createElement('div');

        // Add widget header if set in widget context
        if (SbPanelComponent.isHeaderTextSet(componentRef)) {
            this.addWidgetHeader(componentRef, divElement);
        }
        return divElement;
    }

    private addWidgetLayoutClasses(widgetId: string, sbPanel: SbPanel, htmlElement) {
        // Todo - create widget component- move template code there instead.
        const classes = this.getLayoutClassFromWidgetId(widgetId, sbPanel.widgetLayouts);

        this.applyCustomLayoutForSpecificPanels(sbPanel, classes);

        classes.forEach((c) => {
            this._renderer.addClass(htmlElement, c);
        });
    }

    private applyCustomLayoutForSpecificPanels(sbPanel: SbPanel, classes: string[]) {
        //in regard to a request, ensuring each portfolio-report widget appears on a separate page.
        if (
            sbPanel.panelId.startsWith('portfolio-report') &&
            sbPanel.panelId !== 'portfolio-report-pnl-result-tables'
        ) {
            classes.push('main-div'); //this is defined in sb-print-layout.component.scss
        }
    }

    private static isHeaderTextSet(componentRef: ComponentRef<any>) {
        return (
            componentRef.instance.context?.hasOwnProperty('headerText') &&
            componentRef.instance.context?.headerText
        );
    }

    /**
     * Checks if the header text is excluded from being added dynamically in this component
     * @param componentRef
     * @private
     */
    private static isHeaderTextExcludedHere(componentRef: ComponentRef<any>): boolean {
        //Header of AgDataTable is moved to sb-ag-table in regard to SWIM-4201
        return componentRef.instance.context?.widgetType === SbWidgetType.AgDataTable;
    }

    private addWidgetHeader(componentRef: ComponentRef<any>, divElement) {
        const headerTextFromContext = componentRef.instance.context.headerText;
        const widgetHeader = this._renderer.createElement('div');
        const widgetText = this._renderer.createElement('span');

        this._renderer.addClass(widgetHeader, 'sb-widget-header');
        const widgetHeaderText = this._renderer.createText(headerTextFromContext);
        this._renderer.appendChild(widgetText, widgetHeaderText);

        const widgetHeaderInfo = this._renderer.createElement('span');
        this._renderer.addClass(widgetHeaderInfo, 'sb-header-info');

        this._renderer.appendChild(widgetHeader, widgetText);
        this._renderer.appendChild(widgetHeader, widgetHeaderInfo);
        this._renderer.appendChild(divElement, widgetHeader);
        // Only register header info subscriber if widget header is explicitly set.
        const sub = componentRef.instance.headerInfo.subscribe((hi) =>
            this.widgetHeaderInfoUpdated(hi, widgetHeaderInfo)
        );
        this.subscriptions.push(sub);
    }

    widgetHeaderInfoUpdated(headerInfo: string, widgetHeaderInfo: any) {
        if (widgetHeaderInfo.innerText) {
            widgetHeaderInfo.innerText = headerInfo;
        } else {
            const widgetHeaderInfoText = this._renderer.createText(headerInfo);
            this._renderer.appendChild(widgetHeaderInfo, widgetHeaderInfoText);
        }
    }

    getLayoutClassFromWidgetId(
        widgetId: string,
        widgetLayouts?: SbWidgetLayout[]
    ): string[] {
        if (widgetLayouts) {
            const sbLayout = widgetLayouts.find((lo) => lo.widgetId === widgetId);
            return SbPanelComponent.getLayoutClass(sbLayout);
        }
        return SbPanelComponent.getLayoutClass();
    }

    // Todo - extract to utility method - being used here and in grid-component.ts DRY
    static getLayoutClass(sbLayout?: SbLayout): string[] {
        if (!sbLayout) {
            return ['col-12'];
        }
        if (sbLayout.width) {
            const width = `col-${sbLayout.width || 12}`;
            return [width];
        } else {
            const widthSm = `col-sm-${sbLayout.widthSm || 12}`;
            const widthMd = `col-md-${sbLayout.widthMd || 6}`;
            const widthLg = `col-lg-${sbLayout.widthLg || 4}`;
            const widthXl = `col-xl-${sbLayout.widthXl || 4}`;
            return [`col-${sbLayout.widthSm || 12}`, widthSm, widthMd, widthLg, widthXl];
        }
    }

    formCancel(event) {
        this.location.back();
    }

    btnPrint(event: any) {
        this.printService.changePrintStatus(true);
        const payload: SbEvent = {
            source: this.panelId,
            topic: SbEventTopic.Print,
            payload: 'Preview'
        };
        this.eventBus.emit(payload);
    }

    @HostListener('window:afterprint', ['$event'])
    onAfterPrint(event) {
        this.donePrinting();
    }
    donePrinting() {
        const payload: SbEvent = {
            source: this.panelId,
            topic: SbEventTopic.Print,
            payload: 'PrintingDone'
        };
        this.eventBus.emit(payload);
    }

    areAllGridsEmpty(panelId: string): boolean {
        const panelEvents = Object.values(this.gridDataEvents).filter(
            (event) => event.panelId === panelId
        );
        return panelEvents.every((event) => event.isEmpty);
    }
}
